import React from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import LibraryMusicOutlinedIcon from '@mui/icons-material/LibraryMusicOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  getDefaultNavigationPath,
  type NavigationRoleView,
  persistNavigationRoleView,
} from '../utils/navigationRoleView';

interface NavigationRoleSwitcherProps {
  mini: boolean;
  activeView: NavigationRoleView;
  availableViews: NavigationRoleView[];
  userRoles: string[];
  experimentalFeaturesEnabled: boolean;
  onSwitch: (view: NavigationRoleView) => void;
}

const VIEW_META: Record<
  NavigationRoleView,
  { labelKey: string; defaultLabel: string; Icon: typeof AdminPanelSettingsOutlinedIcon }
> = {
  USER: {
    labelKey: 'navigation.switchToMusicianView',
    defaultLabel: 'Switch to Musician View',
    Icon: LibraryMusicOutlinedIcon,
  },
  STAFF: {
    labelKey: 'navigation.switchToAdminView',
    defaultLabel: 'Switch to Admin View',
    Icon: BusinessCenterOutlinedIcon,
  },
  SUPER_ADMIN: {
    labelKey: 'navigation.switchToSuperAdminView',
    defaultLabel: 'Switch to Super Admin View',
    Icon: AdminPanelSettingsOutlinedIcon,
  },
};

const switchButtonSx = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: 1,
  px: 1.25,
  py: 0.75,
  borderRadius: '6px',
  color: 'rgba(255, 255, 255, 0.72)',
  textAlign: 'left' as const,
  transition: 'background-color 0.18s ease, color 0.18s ease, transform 0.18s ease',
  '&:hover': {
    backgroundColor: 'rgba(78, 103, 235, 0.12)',
    color: 'rgba(255, 255, 255, 0.92)',
  },
  '&:active': {
    transform: 'translateX(1px)',
  },
};

const NavigationRoleSwitcher: React.FC<NavigationRoleSwitcherProps> = ({
  mini,
  activeView,
  availableViews,
  userRoles,
  experimentalFeaturesEnabled,
  onSwitch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const otherViews = availableViews.filter((view) => view !== activeView);
  if (otherViews.length === 0) {
    return null;
  }

  const handleSwitch = (view: NavigationRoleView) => {
    persistNavigationRoleView(view);
    onSwitch(view);
    navigate(getDefaultNavigationPath(view, userRoles, experimentalFeaturesEnabled));
  };

  return (
    <Box
      className="navigation-role-switcher"
      sx={{
        borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        backgroundColor: '#181A22',
        backgroundImage:
          'linear-gradient(180deg, rgba(24, 26, 34, 0) 0%, rgba(78, 103, 235, 0.06) 100%)',
        px: mini ? 0.5 : 1,
        pt: 1,
        pb: 1.25,
        width: '100%',
        flexShrink: 0,
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.22)',
      }}
    >
      {!mini && (
        <Typography
          component="p"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            mb: 0.75,
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.42)',
          }}
        >
          <SwapHorizIcon sx={{ fontSize: '0.85rem', opacity: 0.8 }} />
          {t('navigation.switchView', 'Switch view')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {otherViews.map((view) => {
          const { labelKey, defaultLabel, Icon } = VIEW_META[view];
          const label = t(labelKey, defaultLabel);

          const button = (
            <ButtonBase
              key={view}
              onClick={() => handleSwitch(view)}
              aria-label={label}
              sx={{
                ...switchButtonSx,
                justifyContent: mini ? 'center' : 'flex-start',
                px: mini ? 0.75 : 1.25,
              }}
            >
              <Icon sx={{ fontSize: '1.05rem', flexShrink: 0, opacity: 0.88 }} />
              {!mini && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.25,
                  }}
                >
                  {label}
                </Typography>
              )}
            </ButtonBase>
          );

          if (mini) {
            return (
              <Tooltip key={view} title={label} placement="right" arrow>
                {button}
              </Tooltip>
            );
          }

          return button;
        })}
      </Box>
    </Box>
  );
};

export default NavigationRoleSwitcher;
